import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Chip
} from '@mui/material';
import { ExpertListItemDto, CreateAppointmentDto, AvailableSlot } from '../../types/expert';
import { getAvailableSlots, createAppointment, getAvailableDays } from '../../services/expert.service';
import { getErrorMessage } from '@/utils/api-response.util';
import { format, parseISO, isBefore, startOfMonth } from 'date-fns';
import CustomMonthCalendar from '../common/CustomMonthCalendar.tsx'; // Import the new calendar component
import { ModalIndices } from '../../common/i-doc/i-doc.registry.ts';
import { useAuth } from '@/hooks/useAuth';
import { toastService } from '@/services/toast.service';

interface ConsultationRequestModalProps {
  expert: ExpertListItemDto;
  open: boolean;
  onClose: (isSuccess: boolean) => void;
}

export const ConsultationRequestModal: React.FC<ConsultationRequestModalProps> = ({ expert, open, onClose }) => {
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [currentMonthDisplay, setCurrentMonthDisplay] = useState<string>(format(new Date(), 'yyyy-MM'));

  const [availableDays, setAvailableDays] = useState<string[]>([]);
  const [daysLoading, setDaysLoading] = useState(false);

  const [slots, setSlots] = useState<AvailableSlot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState<string | null>(null);

  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [clientNotes, setClientNotes] = useState('');

  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingError, setBookingError] = useState<string | null>(null);

  const resetState = useCallback(() => {
    const today = new Date();
    setSelectedDate(today);
    setCurrentMonthDisplay(format(today, 'yyyy-MM'));
    setAvailableDays([]);
    setSlots([]);
    setSlotsLoading(false);
    setSlotsError(null);
    setSelectedSlot(null);
    setClientNotes('');
    setBookingLoading(false);
    setBookingError(null);
  }, []);

  const fetchAvailableDays = useCallback(async (monthString: string) => {
    setDaysLoading(true);
    try {
      const days = await getAvailableDays(expert.id, monthString);
      setAvailableDays(days);
    } catch (err) {
      console.error("Failed to fetch available days", err);
      setAvailableDays([]); // Ensure no highlights on error
    } finally {
      setDaysLoading(false);
    }
  }, [expert.id]);

  const fetchSlots = useCallback(async (date: Date | null) => {
    if (!date) return;

    setSlotsLoading(true);
    setSlotsError(null);
    setSelectedSlot(null);
    setSlots([]);
    try {
      const dateString = format(date, 'yyyy-MM-dd');
      // Явно передаємо clientId — щоб бек врахував конфлікти клієнта (інші зустрічі на той самий час).
      const availableSlots = await getAvailableSlots(expert.id, dateString, user?.id);
      setSlots(availableSlots);
    } catch (err) {
      setSlotsError('Не вдалося завантажити доступні слоти.');
      console.error(err);
    } finally {
      setSlotsLoading(false);
    }
  }, [expert.id]);

  useEffect(() => {
    if (open) {
      const today = new Date();
      resetState();
      setSelectedDate(today);
      setCurrentMonthDisplay(format(today, 'yyyy-MM'));
      fetchAvailableDays(format(startOfMonth(today), 'yyyy-MM'));
      // Fetch slots for today initially, regardless of availability, to show all days active
      fetchSlots(today);
    }
  }, [open, resetState, fetchAvailableDays, fetchSlots]);

  useEffect(() => {
    if (open && selectedDate) {
      // Only fetch slots if the selected date is one of the highlighted (available) days
      const selectedDateString = format(selectedDate, 'yyyy-MM-dd');
      if (availableDays.includes(selectedDateString)) {
        fetchSlots(selectedDate);
      } else {
        // If a date is selected that is not in availableDays (e.g., due to error or initial load),
        // we still want to allow selection but clear slots if it's not truly available.
        // This ensures all days are selectable, but slots only show for truly available days.
        setSlots([]);
        setSelectedSlot(null);
      }
    }
  }, [open, selectedDate, availableDays, fetchSlots]);

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
  };

  const handleMonthChange = (month: string) => {
    setCurrentMonthDisplay(month);
    fetchAvailableDays(month);
  };

  const handleSubmit = async () => {
    if (!selectedSlot) {
      setBookingError('Будь ласка, виберіть час для запису.');
      return;
    }

    setBookingLoading(true);
    setBookingError(null);
    try {
      const appointmentData: CreateAppointmentDto = {
        scheduledAt: selectedSlot,
        clientNotes: clientNotes,
      };
      await createAppointment(expert.id, appointmentData);
      toastService.success('Запис створено. Очікуйте підтвердження від експерта.');
      onClose(true);
    } catch (err) {
      setBookingError(getErrorMessage(err, 'Не вдалося створити запис. Спробуйте ще раз.'));
      console.error(err);
    } finally {
      setBookingLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} fullWidth maxWidth="md" data-i-doc={ModalIndices.CONSULTATION_REQUEST}>
      <DialogTitle>Запис на консультацію до {expert.name}</DialogTitle>
      <DialogContent dividers>
        {bookingError && <Alert severity="error" sx={{ mb: 2 }}>{bookingError}</Alert>}

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>1. Виберіть дату</Typography>
            <CustomMonthCalendar
              currentMonth={currentMonthDisplay}
              onMonthChange={handleMonthChange}
              selectedDate={selectedDate}
              onDateChange={handleDateChange}
              highlightedDates={availableDays}
              disabledDates={[]}
            />
            {daysLoading && <CircularProgress size={24} sx={{ mt: 2 }} />}
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>2. Виберіть час</Typography>
            {slotsLoading ? (
              <CircularProgress />
            ) : slotsError ? (
              <Alert severity="error">{slotsError}</Alert>
            ) : (() => {
              const now = new Date();
              const visibleSlots = slots.filter(s => !isBefore(parseISO(s.startTime), now));
              const hasAvailable = visibleSlots.some(s => s.isAvailable);
              const allPast = slots.length > 0 && visibleSlots.length === 0;
              if (allPast) {
                return <Typography color="text.secondary">На сьогодні час для запису вичерпано.</Typography>;
              }
              if (visibleSlots.length > 0) {
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {visibleSlots.map((slot, index) => (
                      <Chip
                        key={`${slot.startTime}-${index}`}
                        label={format(parseISO(slot.startTime), 'HH:mm')}
                        onClick={() => slot.isAvailable && setSelectedSlot(slot.startTime)}
                        disabled={!slot.isAvailable}
                        color={selectedSlot === slot.startTime ? 'primary' : 'default'}
                        variant={selectedSlot === slot.startTime ? 'filled' : 'outlined'}
                      />
                    ))}
                    {!hasAvailable && (
                      <Typography variant="caption" color="text.secondary" sx={{ width: '100%', mt: 0.5 }}>
                        Усі слоти на цей день зайняті.
                      </Typography>
                    )}
                  </Box>
                );
              }
              return <Typography color="text.secondary">На вибрану дату немає вільних слотів.</Typography>;
            })()}

            {selectedSlot && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>3. Додайте коментар (необов'язково)</Typography>
                <TextField
                  label="Ваші нотатки для експерта"
                  value={clientNotes}
                  onChange={(e) => setClientNotes(e.target.value)}
                  multiline
                  rows={3}
                  fullWidth
                />
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: '16px 24px' }}>
        <Button onClick={() => onClose(false)} disabled={bookingLoading}>Скасувати</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!selectedSlot || bookingLoading}>
          {bookingLoading ? <CircularProgress size={24} /> : 'Забронювати'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
